import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { ForbiddenState } from '@/components/states';
import { PageHeader } from '@/components/layout/PageHeader';
import { Placeholder } from '@/pages/Placeholder';
import { AuthLayout } from '@/features/auth/AuthLayout';
import { LoginPage } from '@/features/auth/LoginPage';
import {
  ActivatePage,
  ChangePasswordPage,
  ForgotPasswordPage,
  ResetPasswordPage,
} from '@/features/auth/PasswordPages';
import { SessionsPage } from '@/features/auth/SessionsPage';
import {
  GuestOnly,
  RequireAnyPermission,
  RequireAppAccess,
  RequireAuth,
  RequirePermission,
} from './guards';
import { paths } from './paths';

/**
 * The people module is lazily loaded as ONE group.
 *
 * Its screens share the directory, the pickers and the org-chart shaping, so splitting
 * them per route would download most of it several times over. A route group is the
 * right granularity — per-route splitting of a module is how a product ends up with
 * fifty chunks and a slower first navigation into each.
 */
const People = {
  EmployeeList: lazy(() =>
    import('@/features/people/EmployeeListPage').then((m) => ({ default: m.EmployeeListPage })),
  ),
  EmployeeDetail: lazy(() =>
    import('@/features/people/EmployeeDetailPage').then((m) => ({
      default: m.EmployeeDetailPage,
    })),
  ),
  EmployeeForm: lazy(() =>
    import('@/features/people/EmployeeFormPage').then((m) => ({ default: m.EmployeeFormPage })),
  ),
  Departments: lazy(() =>
    import('@/features/people/DepartmentsPage').then((m) => ({ default: m.DepartmentsPage })),
  ),
  OrgChart: lazy(() =>
    import('@/features/people/OrgChartPage').then((m) => ({ default: m.OrgChartPage })),
  ),
  MyTeam: lazy(() =>
    import('@/features/people/MyTeamPage').then((m) => ({ default: m.MyTeamPage })),
  ),
  MyProfile: lazy(() =>
    import('@/features/people/MyProfilePage').then((m) => ({ default: m.MyProfilePage })),
  ),
};

const Notifications = {
  Centre: lazy(() =>
    import('@/features/notifications/NotificationsPage').then((m) => ({
      default: m.NotificationsPage,
    })),
  ),
  Preferences: lazy(() =>
    import('@/features/notifications/PreferencesPage').then((m) => ({
      default: m.PreferencesPage,
    })),
  ),
};

const Work = {
  Approvals: lazy(() =>
    import('@/features/approvals/ApprovalsPage').then((m) => ({ default: m.ApprovalsPage })),
  ),
  Tasks: lazy(() =>
    import('@/features/tasks/TasksPage').then((m) => ({ default: m.TasksPage })),
  ),
  WorkReports: lazy(() =>
    import('@/features/workReports/WorkReportsPage').then((m) => ({
      default: m.WorkReportsPage,
    })),
  ),
};

const Records = {
  Documents: lazy(() =>
    import('@/features/records/DocumentsPage').then((m) => ({ default: m.DocumentsPage })),
  ),
  Onboarding: lazy(() =>
    import('@/features/records/OnboardingPage').then((m) => ({ default: m.OnboardingPage })),
  ),
  Holidays: lazy(() =>
    import('@/features/records/HolidaysPage').then((m) => ({ default: m.HolidaysPage })),
  ),
};

const Announcements = {
  Board: lazy(() =>
    import('@/features/announcements/AnnouncementsPage').then((m) => ({
      default: m.AnnouncementsPage,
    })),
  ),
  Policy: lazy(() =>
    import('@/features/announcements/NotificationPolicyPage').then((m) => ({
      default: m.NotificationPolicyPage,
    })),
  ),
};

const Insights = {
  Dashboard: lazy(() =>
    import('@/features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
  ),
  Reports: lazy(() =>
    import('@/features/dashboard/ReportsPage').then((m) => ({ default: m.ReportsPage })),
  ),
  WorkSessions: lazy(() =>
    import('@/features/workSessions/WorkSessionsPage').then((m) => ({
      default: m.WorkSessionsPage,
    })),
  ),
};

const TimeLeave = {
  Layout: lazy(() =>
    import('@/features/time/TimeLeaveLayout').then((m) => ({ default: m.TimeLeaveLayout })),
  ),
  Home: lazy(() =>
    import('@/features/time/TimeLeaveLayout').then((m) => ({ default: m.TimeLeaveHomeRedirect })),
  ),
};

const PeopleHub = {
  Layout: lazy(() =>
    import('@/features/people/PeopleLayout').then((m) => ({ default: m.PeopleLayout })),
  ),
  Directory: lazy(() =>
    import('@/features/people/PeopleDirectoryPage').then((m) => ({
      default: m.PeopleDirectoryPage,
    })),
  ),
};

const Leave = {
  Mine: lazy(() =>
    import('@/features/leave/LeavePage').then((m) => ({ default: m.LeavePage })),
  ),
  Team: lazy(() =>
    import('@/features/leave/TeamLeavePage').then((m) => ({ default: m.TeamLeavePage })),
  ),
  Detail: lazy(() =>
    import('@/features/leave/LeaveDetailPage').then((m) => ({ default: m.LeaveDetailPage })),
  ),
  Types: lazy(() =>
    import('@/features/leave/LeaveTypesPage').then((m) => ({ default: m.LeaveTypesPage })),
  ),
};

const Attendance = {
  Mine: lazy(() =>
    import('@/features/attendance/AttendancePage').then((m) => ({ default: m.AttendancePage })),
  ),
  Team: lazy(() =>
    import('@/features/attendance/TeamAttendancePage').then((m) => ({
      default: m.TeamAttendancePage,
    })),
  ),
};

/**
 * Route tree.
 *
 * Feature screens arrive with their phases; until then each route renders an honest
 * placeholder naming the phase that delivers it. The shell, navigation, breadcrumbs and
 * permission filtering around them are real.
 *
 * The design-system page is lazily loaded — it is a developer reference and should not
 * sit in the initial bundle.
 *
 * Two branches: `AuthLayout` for the signed-out screens and `AppShell` for everything
 * else. `RequireAuth` and `GuestOnly` gate them so a signed-in user never sees the login
 * form and a signed-out one never sees the shell — both of which merely save a wasted
 * render, since the API authorizes every request regardless.
 */

const Offline = lazy(() => import('@/pages/Offline').then((m) => ({ default: m.Offline })));

const Settings = lazy(() =>
  import('@/features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);

const CompanySettings = lazy(() =>
  import('@/features/settings/CompanySettingsPage').then((m) => ({
    default: m.CompanySettingsPage,
  })),
);

const AuditLogs = lazy(() =>
  import('@/features/admin/AuditLogsPage').then((m) => ({ default: m.AuditLogsPage })),
);

const PortalActivity = lazy(() =>
  import('@/features/admin/PortalActivityPage').then((m) => ({ default: m.PortalActivityPage })),
);

const SystemHealth = lazy(() =>
  import('@/features/system/SystemHealthPage').then((m) => ({ default: m.SystemHealthPage })),
);

const Support = {
  Layout: lazy(() =>
    import('@/features/support/SupportLayout').then((m) => ({ default: m.SupportLayout })),
  ),
  Home: lazy(() =>
    import('@/features/support/SupportLayout').then((m) => ({ default: m.SupportHomeRedirect })),
  ),
  MyRequests: lazy(() =>
    import('@/features/support/SupportTicketsPage').then((m) => ({
      default: () => <m.SupportTicketsPage queue="mine" />,
    })),
  ),
  Technical: lazy(() =>
    import('@/features/support/SupportTicketsPage').then((m) => ({
      default: () => <m.SupportTicketsPage queue="technical" />,
    })),
  ),
  HrQueue: lazy(() =>
    import('@/features/support/SupportTicketsPage').then((m) => ({
      default: () => <m.SupportTicketsPage queue="hr" />,
    })),
  ),
  Detail: lazy(() =>
    import('@/features/support/SupportTicketDetailPage').then((m) => ({
      default: m.SupportTicketDetailPage,
    })),
  ),
  AlertSettings: lazy(() =>
    import('@/features/support/SupportNotificationSettingsPage').then((m) => ({
      default: m.SupportNotificationSettingsPage,
    })),
  ),
};

const SUPPORT_ACCESS_RULES = [
  { permission: 'support.tickets.read' as const, scope: 'own' as const },
  { permission: 'support.tickets.readTechnical' as const, scope: 'system' as const },
  { permission: 'support.tickets.readHr' as const, scope: 'organization' as const },
];

const DesignSystem = lazy(() =>
  import('@/pages/DesignSystem').then((m) => ({ default: m.DesignSystem })),
);

/** One Suspense boundary shape for every lazy route, rather than six copies of it. */
function Lazy({ element }: { element: React.ReactNode }) {
  return (
    <Suspense fallback={<div className="py-16 text-center text-fg-muted">Loading…</div>}>
      {element}
    </Suspense>
  );
}

function LegacyLeaveDetailRedirect() {
  const { id = '' } = useParams();
  if (/^[0-9a-f]{24}$/i.test(id)) {
    return <Navigate to={paths.leaveDetail(id)} replace />;
  }
  return <Navigate to={paths.leave} replace />;
}

function NotFound() {
  return (
    <PageHeader
      title="Page not found"
      description="That page does not exist. Check the address, or use the navigation to find what you need."
    />
  );
}

function Forbidden() {
  return (
    <>
      <PageHeader title="Access denied" />
      <ForbiddenState />
    </>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Signed-out screens */}
      <Route element={<GuestOnly />}>
        <Route element={<AuthLayout />}>
          <Route path={paths.login} element={<LoginPage />} />
          <Route path={paths.forgotPassword} element={<ForgotPasswordPage />} />
          <Route path={paths.activate} element={<ActivatePage />} />
          <Route path={paths.resetPassword} element={<ResetPasswordPage />} />
        </Route>
      </Route>

      <Route element={<RequireAuth />}>
        <Route element={<AuthLayout />}>
          <Route path={paths.changePassword} element={<ChangePasswordPage />} />
        </Route>

        <Route element={<RequireAppAccess />}>
          <Route element={<AppShell />}>
          <Route index element={<Navigate to={paths.dashboard} replace />} />

          <Route path={paths.dashboard} element={<Lazy element={<Insights.Dashboard />} />} />

          <Route path={paths.employees} element={<Lazy element={<PeopleHub.Layout />} />}>
            <Route index element={<Lazy element={<PeopleHub.Directory />} />} />
            <Route
              path="documents"
              element={<RequirePermission permission="documents.read" scope="own" />}
            >
              <Route index element={<Lazy element={<Records.Documents />} />} />
            </Route>
            <Route
              path="onboarding"
              element={<RequirePermission permission="onboarding.read" scope="own" />}
            >
              <Route index element={<Lazy element={<Records.Onboarding />} />} />
            </Route>
          </Route>
          <Route element={<RequirePermission permission="employees.create" />}>
            <Route path={paths.employeeNew} element={<Lazy element={<People.EmployeeForm />} />} />
          </Route>
          <Route path={paths.employee(':id')} element={<Lazy element={<People.EmployeeDetail />} />} />
          <Route
            path={`${paths.employee(':id')}/edit`}
            element={<Lazy element={<People.EmployeeForm />} />}
          />

          <Route path={paths.me} element={<Lazy element={<People.MyProfile />} />} />
          <Route path={paths.myTeam} element={<Lazy element={<People.MyTeam />} />} />
          <Route path={paths.organization} element={<Lazy element={<People.OrgChart />} />} />
          <Route path={paths.departments} element={<Lazy element={<People.Departments />} />} />

          <Route path={paths.time} element={<Lazy element={<TimeLeave.Layout />} />}>
            <Route index element={<Lazy element={<TimeLeave.Home />} />} />
            <Route
              path="attendance"
              element={<RequirePermission permission="attendance.punch" />}
            >
              <Route index element={<Lazy element={<Attendance.Mine />} />} />
              <Route
                path="team"
                element={<RequirePermission permission="attendance.read" scope="reports" />}
              >
                <Route index element={<Lazy element={<Attendance.Team />} />} />
              </Route>
            </Route>
            <Route path="leave" element={<RequirePermission permission="leave.read" />}>
              <Route index element={<Lazy element={<Leave.Mine />} />} />
              <Route
                path="team"
                element={<RequirePermission permission="leave.read" scope="reports" />}
              >
                <Route index element={<Lazy element={<Leave.Team />} />} />
              </Route>
              <Route path=":id" element={<Lazy element={<Leave.Detail />} />} />
            </Route>
            <Route
              path="leave-types"
              element={<RequirePermission permission="leave.configure" scope="system" />}
            >
              <Route index element={<Lazy element={<Leave.Types />} />} />
            </Route>
            <Route path="holidays" element={<RequirePermission permission="holidays.read" />}>
              <Route index element={<Lazy element={<Records.Holidays />} />} />
            </Route>
          </Route>

          {/* Legacy URLs from before the hub merge */}
          <Route path="/attendance/team" element={<Navigate to={paths.teamAttendance} replace />} />
          <Route path="/attendance" element={<Navigate to={paths.attendance} replace />} />
          <Route path="/leave/types" element={<Navigate to={paths.leaveTypes} replace />} />
          <Route path="/leave/team" element={<Navigate to={paths.teamLeave} replace />} />
          <Route path="/leave/:id" element={<LegacyLeaveDetailRedirect />} />
          <Route path="/leave" element={<Navigate to={paths.leave} replace />} />
          <Route path="/holidays" element={<Navigate to={paths.holidays} replace />} />
          <Route path="/documents" element={<Navigate to={paths.documents} replace />} />
          <Route path="/onboarding" element={<Navigate to={paths.onboarding} replace />} />
          <Route path={paths.approvals} element={<Lazy element={<Work.Approvals />} />} />

          <Route path={paths.tasks} element={<Lazy element={<Work.Tasks />} />} />
          <Route
            path={paths.taskBoard}
            element={<Lazy element={<Work.Tasks initialView="board" />} />}
          />
          <Route path={paths.workReports} element={<Lazy element={<Work.WorkReports />} />} />

          <Route
            path={paths.announcements}
            element={<Lazy element={<Announcements.Board />} />}
          />
          <Route
            path={paths.notificationPolicy}
            element={<RequirePermission permission="notifications.configurePolicy" scope="system" />}
          >
            <Route index element={<Lazy element={<Announcements.Policy />} />} />
          </Route>
          <Route
            path={paths.notificationPreferences}
            element={<Lazy element={<Notifications.Preferences />} />}
          />
          <Route
            path={paths.notifications}
            element={<Lazy element={<Notifications.Centre />} />}
          />
          <Route
            path={paths.workSessions}
            element={<Lazy element={<Insights.WorkSessions />} />}
          />

          <Route path={paths.reports} element={<RequirePermission permission="analytics.readScoped" scope="reports" />}>
            <Route index element={<Lazy element={<Insights.Reports />} />} />
          </Route>

          <Route path={paths.sessions} element={<SessionsPage />} />

          <Route path={paths.settings} element={<Lazy element={<Settings />} />} />
          <Route path={paths.offline} element={<Lazy element={<Offline />} />} />
          <Route element={<RequirePermission permission="settings.read" scope="system" />}>
            <Route
              path={paths.companySettings}
              element={<Lazy element={<CompanySettings />} />}
            />
          </Route>
          <Route path={paths.roles} element={<Placeholder title="Roles & Permissions" phase="Phase 3" />} />
          <Route path={paths.systemHealth} element={<Lazy element={<SystemHealth />} />} />
          <Route element={<RequirePermission permission="audit.read" scope="system" />}>
            <Route path={paths.portalActivity} element={<Lazy element={<PortalActivity />} />} />
          </Route>
          <Route element={<RequireAnyPermission rules={SUPPORT_ACCESS_RULES} />}>
            <Route path={paths.support} element={<Lazy element={<Support.Layout />} />}>
              <Route index element={<Lazy element={<Support.Home />} />} />
              <Route element={<RequirePermission permission="support.tickets.read" scope="own" />}>
                <Route path="my-requests" element={<Lazy element={<Support.MyRequests />} />} />
              </Route>
              <Route
                element={
                  <RequirePermission permission="support.tickets.readTechnical" scope="system" />
                }
              >
                <Route path="technical" element={<Lazy element={<Support.Technical />} />} />
              </Route>
              <Route
                element={<RequirePermission permission="support.tickets.readHr" scope="organization" />}
              >
                <Route path="company" element={<Lazy element={<Support.HrQueue />} />} />
              </Route>
              <Route path="tickets/:id" element={<Lazy element={<Support.Detail />} />} />
            </Route>
          </Route>
          <Route element={<RequirePermission permission="support.notifications.configure" scope="system" />}>
            <Route
              path={paths.supportAlertSettings}
              element={<Lazy element={<Support.AlertSettings />} />}
            />
          </Route>
          <Route element={<RequirePermission permission="audit.read" scope="system" />}>
            <Route path={paths.auditLogs} element={<Lazy element={<AuditLogs />} />} />
          </Route>

          <Route
            path={paths.designSystem}
            element={
              <Suspense fallback={<div className="py-16 text-center text-fg-muted">Loading…</div>}>
                <DesignSystem />
              </Suspense>
            }
          />

          <Route path={paths.forbidden} element={<Forbidden />} />
          <Route path="*" element={<NotFound />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
}
